/// <reference path="../types/react-fitty.d.ts" />
import React, { useState, useEffect } from 'react';
import { gameService } from '../firebase/gameService';
import type { GameState, Category, Question, Player, PlayerRole } from '../types/game';
import { v4 as uuidv4 } from 'uuid';
import { ReactFitty } from 'react-fitty';
import { GameBoardGrid } from './GameBoardGrid';
import FinalJeopardy from './FinalJeopardy';
import * as JSX from 'react/jsx-runtime';

// Temporary module declaration for react-textfit types
// @ts-ignore
declare module 'react-textfit';

interface HostDashboardProps {
  gameId: string;
}

function Modal({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="bg-[#222] rounded-xl shadow-2xl p-8 max-w-lg w-full relative">
        <button
          className="absolute top-4 right-4 text-brand-yellow text-2xl font-bold hover:text-white"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}

function playBoardFillSound() {
  const audio = new Audio('/musicandsounds/boardfill.mp3');
  audio.play();
}

export function HostDashboard({ gameId }: HostDashboardProps) {
  const [game, setGame] = useState<GameState | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [playerName, setPlayerName] = useState('');
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalQuestion, setModalQuestion] = useState<Question | null>(null);
  const [modalCategory, setModalCategory] = useState<Category | null>(null);
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [scoring, setScoring] = useState<{ [playerId: string]: 'correct' | 'incorrect' | null }>({});
  const [hasAdvanced, setHasAdvanced] = useState(false);

  useEffect(() => {
    const unsubscribe = gameService.subscribeToGame(gameId, (updatedGame) => {
      setGame(updatedGame);
    });
    return () => unsubscribe();
  }, [gameId]);

  // Get 5 active categories for the current round
  const getActiveCategories = () => {
    if (!game) return [];
    const active = game.categories.filter(cat => cat.isActive !== false);
    if (game.round === 2) {
      return active.slice(5, 10).length === 5 ? active.slice(5, 10) : active.slice(0, 5);
    }
    return active.slice(0, 5);
  };
  const categories = getActiveCategories();
  // If fewer than 5, fill with placeholders
  while (categories.length < 5) {
    categories.push({ id: `placeholder-${categories.length}`, name: '', questions: [], isActive: false, description: '', hasFinalJeopardy: false });
  }

  // Dollar values per round
  const roundValues = game && game.round === 2 ? [200, 400, 600, 800, 1000] : [100, 200, 300, 400, 500];

  // Get questions for each category for the current round (first 5 per category)
  const getQuestionsForCategory = (category: Category) => {
    if (!category.questions) return Array(5).fill(null);
    // For round 2, you might want to use a different set, but for now use the next 5 if available
    if (game && game.round === 1) return category.questions.slice(0, 5);
    return category.questions.slice(5, 10).length === 5 ? category.questions.slice(5, 10) : category.questions.slice(0, 5);
  };

  const handleAdvanceRound = async () => {
    playBoardFillSound();
    if (!game) return;
    if (game.round === 1) {
      await gameService.updateGame(gameId, { round: 2 });
      setHasAdvanced(true);
    } else if (game.round === 2) {
      // Start Final Jeopardy with a random question
      await gameService.startFinalJeopardy(gameId);
    }
  };

  const handleStartGame = async () => {
    playBoardFillSound();
    if (game) {
      await gameService.updateGame(gameId, { status: 'in_progress' });
    }
  };

  const handleRevealQuestion = async (categoryId: string, questionId: string) => {
    await gameService.revealQuestion(gameId, categoryId, questionId);
  };

  const handleMarkQuestionAnswered = async () => {
    if (!game || !selectedQuestion) return;

    const updatedCategories = game.categories.map(category => {
      if (category.id === selectedCategory?.id) {
        return {
          ...category,
          questions: category.questions.map(question =>
            question.id === selectedQuestion.id
              ? { ...question, answered: true }
              : question
          ),
        };
      }
      return category;
    });

    await gameService.updateGame(gameId, {
      categories: updatedCategories,
      currentQuestion: null,
    });
  };

  const handleAddPlayer = async () => {
    if (!playerName.trim() || !game || game.status !== 'waiting') return;
    setAddingPlayer(true);
    const newPlayer: Player = {
      id: uuidv4(),
      name: playerName.trim(),
      score: 0,
      role: 'player' as PlayerRole,
      avatar: 'avatar2.svg', // fallback avatar, should be replaced with unique logic if needed
    };
    await gameService.addPlayer(gameId, newPlayer);
    setPlayerName('');
    setAddingPlayer(false);
  };

  const handleRemovePlayer = async (playerId: string) => {
    if (!game) return;
    const updatedPlayers = game.players.filter(p => p.id !== playerId);
    await gameService.updateGame(gameId, { players: updatedPlayers });
  };

  const openModal = async (category: Category, question: Question, valueIdx: number) => {
    setModalCategory(category);
    // Set the value based on the round and valueIdx
    const value = game!.round === 2
      ? [200, 400, 600, 800, 1000][valueIdx]
      : [100, 200, 300, 400, 500][valueIdx];
    const questionWithValue = { ...question, value };
    setModalQuestion(questionWithValue);
    setAnswerRevealed(!!question.answered);
    setModalOpen(true);
    setScoring({});
    await gameService.updateGame(gameId, {
      currentQuestion: {
        question: questionWithValue,
        categoryName: category.name,
      },
    });
  };

  const closeModal = async () => {
    setModalOpen(false);
    setModalQuestion(null);
    setModalCategory(null);
    setAnswerRevealed(false);
    // Clear currentQuestion in Firebase
    await gameService.updateGame(gameId, { currentQuestion: null });
  };

  const handleRevealAnswer = async () => {
    if (!game || !modalCategory || !modalQuestion) return;
    setAnswerRevealed(true);
    // Mark question as answered in the game state
    const updatedCategories = game.categories.map(category => {
      if (category.id === modalCategory.id) {
        return {
          ...category,
          questions: category.questions.map(q =>
            q.id === modalQuestion.id ? { ...q, answered: true } : q
          ),
        };
      }
      return category;
    });
    // Also update currentQuestion.answered in Firebase
    await gameService.updateGame(gameId, {
      categories: updatedCategories,
      currentQuestion: {
        question: { ...modalQuestion, answered: true },
        categoryName: modalCategory.name,
      },
    });
  };

  const handleScore = async (playerId: string, type: 'correct' | 'incorrect') => {
    if (!game || !modalQuestion) return;
    const player = game.players.find(p => p.id === playerId);
    if (!player) return;
    if (scoring[playerId]) return; // Prevent double scoring
    const value = modalQuestion.value!;
    const newScore = type === 'correct' ? player.score + value : player.score - value;
    // Update player score in Firebase
    await gameService.updatePlayerScore(gameId, playerId, newScore);
    setScoring(prev => ({ ...prev, [playerId]: type }));
  };

  if (!game || !Array.isArray(game.categories) || !Array.isArray(game.players)) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-brand-yellow text-2xl">Loading...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      {game.round === 3 ? (
        <FinalJeopardy
          gameId={gameId}
          game={game}
          onComplete={() => {
            // Handle game completion
            setGame(prev => prev ? { ...prev, status: 'finished' } : null);
          }}
        />
      ) : (
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-8">
          {/* Main Content (Board) */}
          <div className="flex-1">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6 md:gap-0">
              <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-16" />
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                <div className="text-right">
                  <p className="text-brand-yellow text-xl font-bold">Game Code</p>
                  <p className="text-white text-2xl tracking-widest">{game.code}</p>
                </div>
                <div className="text-right">
                  <p className="text-brand-yellow text-xl font-bold">Status</p>
                  <p className="text-white text-2xl">{game.status}</p>
                </div>
                <button
                  onClick={handleStartGame}
                  disabled={game.status !== 'waiting' || game.players.length === 0}
                  className="ml-0 md:ml-8 bg-brand-yellow text-black font-bold px-8 py-4 rounded-xl shadow-lg hover:bg-[#fccd6c]/90 transition-colors text-xl disabled:opacity-50"
                >
                  Start Game
                </button>
                {game && game.round < 3 && (
                  <button
                    onClick={handleAdvanceRound}
                    disabled={game.round === 1 && hasAdvanced}
                    className="ml-0 md:ml-8 bg-brand-yellow text-black font-bold px-8 py-4 rounded-xl shadow-lg hover:bg-[#fccd6c]/90 transition-colors text-xl disabled:opacity-50"
                  >
                    {game.round === 1 ? 'Next Round' : 'Final Jeopardy'}
                  </button>
                )}
              </div>
            </div>

            {/* Jeopardy Board (5x5 grid) */}
            <GameBoardGrid
              categories={categories}
              values={roundValues}
              questions={categories.map(getQuestionsForCategory)}
              onTileClick={(categoryIdx: number, valueIdx: number) => {
                const category = categories[categoryIdx];
                const question = getQuestionsForCategory(category)[valueIdx];
                if (category && question && !question.answered) {
                  openModal(category, question, valueIdx);
                }
              }}
            />
          </div>

          {/* Player Sidebar */}
          <aside className="w-full md:w-80 flex-shrink-0 mt-8 md:mt-0">
            <div className="bg-[#222] rounded-3xl shadow-2xl p-6 flex flex-col gap-6 sticky top-8">
              <div className="flex flex-col gap-4">
                {/* On-Stage Players */}
                <h2 className="text-2xl font-extrabold mb-2 text-brand-yellow flex items-center gap-2">
                  <img src="/images/white-glasses.svg" alt="Players" className="h-7" />
                  On-Stage Players
                </h2>
                <div className="flex flex-col gap-4">
                  {game.players.filter(p => p.role === 'player').map(player => (
                    <div key={player.id} className="flex items-center gap-5 bg-[#222] rounded-2xl px-6 py-4 shadow-xl border-2 border-brand-yellow/70 min-h-[80px]">
                      <div className="h-16 w-16 rounded-full bg-black flex items-center justify-center border-2 border-brand-yellow shadow-md">
                        <img src={`/images/${player.avatar}`} alt="Avatar" className="h-12 w-12" />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-center items-center text-center">
                        <span className="font-extrabold text-brand-yellow text-2xl leading-tight truncate drop-shadow">{player.name}</span>
                        <span className="text-white text-xl font-bold mt-1">${player.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Audience Leaderboard */}
                {game.settings.showAudienceLeaderboard && (
                  <div className="mt-8">
                    <h2 className="text-2xl font-extrabold mb-2 text-brand-yellow flex items-center gap-2">
                      <img src="/images/white-glasses.svg" alt="Audience Leaderboard" className="h-7" />
                      Audience Leaderboard
                    </h2>
                    <div className="flex flex-col gap-4">
                      {game.players.filter(p => p.role === 'audience')
                        .sort((a, b) => b.score - a.score)
                        .map((player, index) => (
                          <div key={player.id} className="flex items-center gap-4 bg-[#333] rounded-2xl px-5 py-4 shadow-lg border-2 border-brand-yellow/40 min-h-[56px]">
                            <div className="h-10 w-10 rounded-full bg-[#28332a] flex items-center justify-center">
                              <span className="text-brand-yellow font-bold text-xl">{index + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-extrabold text-brand-yellow text-lg leading-tight truncate">{player.name}</p>
                              <p className="text-white text-xl font-bold">${player.score}</p>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Host Modal Popup */}
      <Modal open={modalOpen} onClose={closeModal}>
        {modalQuestion && (
          <div className="flex flex-col items-center gap-8">
            <div className="text-brand-yellow text-2xl md:text-3xl font-extrabold text-center mb-2">
              {modalCategory?.name}
            </div>
            <div className="text-white text-2xl md:text-3xl font-bold text-center mb-4">
              {modalQuestion.text}
            </div>
            {/* Show answer to host immediately */}
            <div className="bg-[#333] text-white text-xl md:text-2xl font-bold rounded-lg px-6 py-4 shadow-lg mb-4">
              {modalQuestion.answer}
            </div>
            {answerRevealed ? (
              <div className="bg-[#acd9b7] text-black text-xl md:text-2xl font-bold rounded-lg px-6 py-4 shadow-lg mb-4">
                {modalQuestion.answer}
              </div>
            ) : (
              <button
                className="bg-brand-yellow text-black font-bold px-8 py-4 rounded-lg hover:bg-[#fccd6c]/90 transition-colors text-xl mb-4"
                onClick={handleRevealAnswer}
              >
                Reveal Answer to Audience
              </button>
            )}
            {/* Player Scoring Controls */}
            <div className="w-full flex flex-wrap justify-center gap-4">
              {game.players.filter(p => p.role === 'player').map(player => (
                <div key={player.id} className="flex flex-col items-center bg-[#333] rounded-lg p-4 border border-brand-yellow min-w-[120px]">
                  <div className="h-10 w-10 rounded-full bg-[#28332a] flex items-center justify-center mb-2">
                    <img src={`/images/${player.avatar}`} alt="Avatar" className="h-8 w-8" />
                  </div>
                  <div className="text-brand-yellow font-bold text-center mb-2 text-sm truncate max-w-[80px]">{player.name}</div>
                  <div className="flex gap-2 mt-2">
                    <button
                      className={`h-8 w-8 flex items-center justify-center rounded bg-green-600 text-white text-lg ${scoring[player.id]==='correct' ? 'ring-2 ring-green-400' : ''}`}
                      onClick={() => handleScore(player.id, 'correct')}
                      disabled={!!scoring[player.id]}
                      title="Correct"
                    >
                      <span role="img" aria-label="Correct">✅</span>
                    </button>
                    <button
                      className={`h-8 w-8 flex items-center justify-center rounded bg-red-600 text-white text-lg ${scoring[player.id]==='incorrect' ? 'ring-2 ring-red-400' : ''}`}
                      onClick={() => handleScore(player.id, 'incorrect')}
                      disabled={!!scoring[player.id]}
                      title="Incorrect"
                    >
                      <span role="img" aria-label="Incorrect">❌</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              className="mt-8 bg-[#5d7763] text-white font-bold px-8 py-4 rounded-lg hover:bg-[#5d7763]/90 transition-colors text-xl"
              onClick={closeModal}
            >
              Close
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
} 